/**
 * scratchSound — Web Audio scratching + reveal chime.
 *
 * Same principle as `spinSound.js`: no external audio assets, no
 * network I/O. We synthesise a soft white-noise loop through a
 * lowpass filter for the "scratch" sensation, and a short ascending
 * arpeggio for the "reveal chime". Every call swallows errors so
 * an old browser (or an autoplay-blocked context) never breaks the
 * visual game.
 *
 * Supported values for the `variant` argument (mirrors the vendor
 * wizard's Scratch Sound selector):
 *   "classic"  → warm, low-mid noise + tonal chime
 *   "shimmer"  → brighter noise + higher chime
 *   "none"     → completely silent (returns a no-op controller)
 */

let _audioCtx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  try {
    if (!_audioCtx) {
      _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Autoplay policy: an audio context created outside of a user
    // gesture starts in "suspended". Resuming here is cheap and
    // safe — the actual gesture (pointerdown / click) is what
    // unlocks it in Safari/iOS.
    if (_audioCtx.state === "suspended") {
      _audioCtx.resume().catch(() => {});
    }
    return _audioCtx;
  } catch (err) {
    if (typeof console !== "undefined") {
      console.debug("[scratchSound] AudioContext unavailable", err);
    }
    return null;
  }
}

/**
 * Start a soft, looping scratch texture. Returns a controller with
 * `.stop()` that must be called when the pointer lifts. The parent
 * component owns the lifecycle — this file just synthesises audio.
 */
export function playScratchLoop(variant = "classic") {
  if (variant === "none") {
    return { stop: () => {} };
  }
  const ctx = getCtx();
  if (!ctx) return { stop: () => {} };
  try {
    // Noise buffer — 1 second of pink-ish white noise reused via
    // AudioBufferSourceNode(loop=true). Cheap.
    const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      // Simple pink-ish shape (rolling low-pass by averaging).
      data[i] = (Math.random() * 2 - 1) * 0.7;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = variant === "shimmer" ? 3200 : 1600;
    filter.Q.value = 0.7;

    const gain = ctx.createGain();
    gain.gain.value = 0;
    // Ramp UP quickly so the scratch starts responsive, then hold.
    gain.gain.setTargetAtTime(variant === "shimmer" ? 0.10 : 0.14, ctx.currentTime, 0.02);

    src.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    src.start();

    return {
      stop: () => {
        try {
          gain.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
          setTimeout(() => {
            try {
              src.stop();
            } catch (err) {
              // Source may already be stopped by the browser.
              if (typeof console !== "undefined") {
                console.debug("[scratchSound] loop stop failed", err);
              }
            }
          }, 120);
        } catch (err) {
          if (typeof console !== "undefined") {
            console.debug("[scratchSound] fade-out failed", err);
          }
        }
      },
    };
  } catch (err) {
    if (typeof console !== "undefined") {
      console.debug("[scratchSound] loop init failed", err);
    }
    return { stop: () => {} };
  }
}

/**
 * Play a short reveal chime (a couple of arpeggiated tones). Fires
 * once when the mask crosses the reveal threshold. `variant="none"`
 * is silent.
 */
export function playRevealChime(variant = "classic") {
  if (variant === "none") return;
  const ctx = getCtx();
  if (!ctx) return;
  try {
    // Classic: C5-E5-G5. Shimmer: E5-G5-B5-D6 (brighter major add-9).
    const notes = variant === "shimmer"
      ? [659.25, 783.99, 987.77, 1174.66]
      : [523.25, 659.25, 783.99];
    const now = ctx.currentTime;
    notes.forEach((freq, i) => {
      const t = now + i * 0.09;
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.28, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.5);
    });
  } catch (err) {
    if (typeof console !== "undefined") {
      console.debug("[scratchSound] reveal chime failed", err);
    }
  }
}
